// =============================================================================
// Settings → Public Booking Tab
// =============================================================================
// UI per configurare Tenant.settings.publicBooking. Espone i flag principali
// (enabled, mode, finestre temporali, branding, captcha scaffold) e il link
// pubblico copiabile basato sullo slug del tenant.
// =============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, AlertCircle, Globe, Settings2, Shield, Palette, FileText, ShieldCheck } from 'lucide-react';
import {
  useTenantDetails,
  useUpdatePublicBookingSettings,
} from '../../api/hooks';
import { useToast } from '../../store/toastStore';
import {
  DEFAULT_PUBLIC_BOOKING_SETTINGS,
  type PublicBookingSettings,
  type PublicBookingTheme,
} from '../../types/public-booking';
import {
  Button,
  FormField,
  IconButton,
  LoadingBlock,
  SectionHeader,
  Select,
  SettingsCard,
  TextInput,
  Toggle,
} from './_shared';
import { useReportDirty } from './settingsDirty';

function readPublicBooking(settings: unknown): PublicBookingSettings {
  if (!settings || typeof settings !== 'object') return { ...DEFAULT_PUBLIC_BOOKING_SETTINGS };
  const raw = (settings as Record<string, unknown>).publicBooking as
    | Partial<PublicBookingSettings>
    | undefined;
  if (!raw) return { ...DEFAULT_PUBLIC_BOOKING_SETTINGS };
  return {
    ...DEFAULT_PUBLIC_BOOKING_SETTINGS,
    ...raw,
    branding: { ...DEFAULT_PUBLIC_BOOKING_SETTINGS.branding, ...(raw.branding ?? {}) },
    captcha: { ...DEFAULT_PUBLIC_BOOKING_SETTINGS.captcha, ...(raw.captcha ?? {}) },
  };
}

export function PublicBookingTab() {
  const { t } = useTranslation('settings');
  const toast = useToast();
  const { data: tenant, isLoading } = useTenantDetails();
  const update = useUpdatePublicBookingSettings();
  const [form, setForm] = useState<PublicBookingSettings>(DEFAULT_PUBLIC_BOOKING_SETTINGS);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (tenant) setForm(readPublicBooking(tenant.settings));
  }, [tenant]);

  const publicUrl = useMemo(() => {
    if (!tenant?.slug) return '';
    return `${window.location.origin}/book/${tenant.slug}`;
  }, [tenant?.slug]);

  const handleSave = async () => {
    try {
      await update.mutateAsync(form);
      toast.success(t('publicBooking.saved'));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('publicBooking.saveError');
      toast.error(msg);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Dirty-state: confronta il form con le impostazioni salvate
  const isDirty =
    JSON.stringify(form) !== JSON.stringify(readPublicBooking(tenant?.settings));
  useReportDirty('public-booking', isDirty);

  if (isLoading || !tenant) {
    return <LoadingBlock />;
  }

  return (
    <div className="space-y-6">
      {/* Master switch */}
      <SettingsCard>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900">
              {t('publicBooking.enabled.title')}
            </h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">
              {t('publicBooking.enabled.description')}
            </p>
          </div>
          <Toggle
            checked={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            aria-label={t('publicBooking.enabled.title')}
          />
        </div>

        {form.enabled && (
          <div className="mt-5 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2 font-semibold">
              <Globe className="w-3.5 h-3.5" />
              {t('publicBooking.publicLink')}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 font-mono truncate">
                {publicUrl}
              </code>
              <IconButton
                icon={copied ? Check : Copy}
                tone={copied ? 'primary' : 'neutral'}
                label={t('publicBooking.copy')}
                onClick={handleCopy}
              />
            </div>
          </div>
        )}
      </SettingsCard>

      {/* Modalità */}
      <SettingsCard>
        <SectionHeader
          icon={Settings2}
          title={t('publicBooking.mode.title')}
          description={t('publicBooking.mode.description')}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ModeRadio
            value="auto"
            current={form.mode}
            onChange={(v) => setForm({ ...form, mode: v })}
            label={t('publicBooking.mode.auto.label')}
            description={t('publicBooking.mode.auto.description')}
          />
          <ModeRadio
            value="approval"
            current={form.mode}
            onChange={(v) => setForm({ ...form, mode: v })}
            label={t('publicBooking.mode.approval.label')}
            description={t('publicBooking.mode.approval.description')}
          />
        </div>
      </SettingsCard>

      {/* Finestre temporali e regole guest */}
      <SettingsCard>
        <SectionHeader icon={Shield} title={t('publicBooking.rules.title')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label={t('publicBooking.rules.minAdvanceHours')}>
            <TextInput
              type="number"
              min={0}
              max={720}
              value={form.minAdvanceHours}
              onChange={(e) => setForm({ ...form, minAdvanceHours: Number(e.target.value) })}
            />
          </FormField>
          <FormField label={t('publicBooking.rules.maxAdvanceDays')}>
            <TextInput
              type="number"
              min={1}
              max={365}
              value={form.maxAdvanceDays}
              onChange={(e) => setForm({ ...form, maxAdvanceDays: Number(e.target.value) })}
            />
          </FormField>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {t('publicBooking.rules.allowGuestCustomers.title')}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {t('publicBooking.rules.allowGuestCustomers.description')}
            </p>
          </div>
          <Toggle
            checked={form.allowGuestCustomers}
            onChange={(v) => setForm({ ...form, allowGuestCustomers: v })}
            aria-label={t('publicBooking.rules.allowGuestCustomers.title')}
          />
        </div>
      </SettingsCard>

      {/* Branding */}
      <SettingsCard>
        <SectionHeader
          icon={Palette}
          title={t('publicBooking.branding.title')}
          description={t('publicBooking.branding.description')}
        />
        <div className="space-y-4">
          <FormField label={t('publicBooking.branding.displayName')}>
            <TextInput
              value={form.branding.displayName ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  branding: { ...form.branding, displayName: e.target.value || null },
                })
              }
              placeholder={tenant.name}
            />
          </FormField>
          <FormField
            label={t('publicBooking.branding.logoUrl')}
            hint={t('publicBooking.branding.logoUrlHint')}
          >
            <TextInput
              type="url"
              value={form.branding.logoUrl ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  branding: { ...form.branding, logoUrl: e.target.value || null },
                })
              }
              placeholder="https://..."
            />
          </FormField>
          <FormField label={t('publicBooking.branding.primaryColor')}>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.branding.primaryColor ?? '#10B981'}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, primaryColor: e.target.value },
                  })
                }
                className="h-10 w-12 rounded-lg border border-gray-300 cursor-pointer"
              />
              <TextInput
                value={form.branding.primaryColor ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, primaryColor: e.target.value || null },
                  })
                }
                placeholder="#10B981"
                className="font-mono"
              />
            </div>
          </FormField>

          {/* Stile pagina: minimal vs hero */}
          <FormField label={t('publicBooking.branding.theme')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ThemeRadio
                value="minimal"
                current={form.branding.theme}
                onChange={(v) => setForm({ ...form, branding: { ...form.branding, theme: v } })}
                label={t('publicBooking.branding.themeMinimal')}
                description={t('publicBooking.branding.themeMinimalHint')}
              />
              <ThemeRadio
                value="hero"
                current={form.branding.theme}
                onChange={(v) => setForm({ ...form, branding: { ...form.branding, theme: v } })}
                label={t('publicBooking.branding.themeHero')}
                description={t('publicBooking.branding.themeHeroHint')}
              />
            </div>
          </FormField>

          {/* Messaggio di benvenuto (mostrato in entrambi gli stili, in evidenza nell'hero) */}
          <FormField label={t('publicBooking.branding.headline')}>
            <TextInput
              value={form.branding.headline ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  branding: { ...form.branding, headline: e.target.value || null },
                })
              }
              placeholder={t('publicBooking.branding.headlinePlaceholder')}
            />
          </FormField>
          <FormField label={t('publicBooking.branding.subheadline')}>
            <TextInput
              value={form.branding.subheadline ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  branding: { ...form.branding, subheadline: e.target.value || null },
                })
              }
              placeholder={t('publicBooking.branding.subheadlinePlaceholder')}
            />
          </FormField>

          {/* Copertina: rilevante solo per lo stile hero */}
          {form.branding.theme === 'hero' && (
            <FormField label={t('publicBooking.branding.coverImageUrl')}>
              <TextInput
                type="url"
                value={form.branding.coverImageUrl ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    branding: { ...form.branding, coverImageUrl: e.target.value || null },
                  })
                }
                placeholder="https://..."
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('publicBooking.branding.coverImageHint')}
              </p>
            </FormField>
          )}
        </div>
      </SettingsCard>

      {/* Termini / Privacy */}
      <SettingsCard>
        <SectionHeader icon={FileText} title={t('publicBooking.legal.title')} />
        <div className="space-y-4">
          <FormField label={t('publicBooking.legal.termsUrl')}>
            <TextInput
              type="url"
              value={form.termsUrl ?? ''}
              onChange={(e) => setForm({ ...form, termsUrl: e.target.value || null })}
              placeholder="https://..."
            />
          </FormField>
          <FormField label={t('publicBooking.legal.privacyUrl')}>
            <TextInput
              type="url"
              value={form.privacyUrl ?? ''}
              onChange={(e) => setForm({ ...form, privacyUrl: e.target.value || null })}
              placeholder="https://..."
            />
          </FormField>
        </div>
      </SettingsCard>

      {/* CAPTCHA scaffold */}
      <SettingsCard>
        <SectionHeader icon={ShieldCheck} title={t('publicBooking.captcha.title')} />
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            {t('publicBooking.captcha.notImplementedWarning')}
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-900">
              {t('publicBooking.captcha.enabled')}
            </span>
            <Toggle
              checked={form.captcha.enabled}
              onChange={(v) =>
                setForm({ ...form, captcha: { ...form.captcha, enabled: v } })
              }
              aria-label={t('publicBooking.captcha.enabled')}
            />
          </div>
          {form.captcha.enabled && (
            <>
              <FormField label={t('publicBooking.captcha.provider')}>
                <Select
                  value={form.captcha.provider ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      captcha: {
                        ...form.captcha,
                        provider: (e.target.value || null) as PublicBookingSettings['captcha']['provider'],
                      },
                    })
                  }
                >
                  <option value="">{t('publicBooking.captcha.selectProvider')}</option>
                  <option value="hcaptcha">hCaptcha</option>
                  <option value="turnstile">Cloudflare Turnstile</option>
                  <option value="recaptcha-v3">reCAPTCHA v3</option>
                </Select>
              </FormField>
              <FormField label={t('publicBooking.captcha.siteKey')}>
                <TextInput
                  value={form.captcha.siteKey ?? ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      captcha: { ...form.captcha, siteKey: e.target.value || null },
                    })
                  }
                  className="font-mono"
                />
              </FormField>
            </>
          )}
        </div>
      </SettingsCard>

      {/* Save */}
      <div className="flex items-center justify-end gap-4 sticky bottom-0 -mx-6 px-6 py-3 bg-gray-50/95 backdrop-blur border-t border-gray-200">
        {isDirty && (
          <span className="flex items-center gap-1.5 text-sm text-amber-600 mr-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            {t('organization.unsavedChanges')}
          </span>
        )}
        <Button onClick={handleSave} loading={update.isPending} disabled={!isDirty}>
          {t('publicBooking.save')}
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

function ModeRadio({
  value,
  current,
  onChange,
  label,
  description,
}: {
  value: 'auto' | 'approval';
  current: 'auto' | 'approval';
  onChange: (v: 'auto' | 'approval') => void;
  label: string;
  description: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`text-left p-4 rounded-lg border-2 transition-all ${
        active
          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/10'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className={`text-sm font-semibold ${active ? 'text-emerald-700' : 'text-gray-900'}`}>
        {label}
      </div>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>
    </button>
  );
}

function ThemeRadio({
  value,
  current,
  onChange,
  label,
  description,
}: {
  value: PublicBookingTheme;
  current: PublicBookingTheme;
  onChange: (v: PublicBookingTheme) => void;
  label: string;
  description: string;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`text-left p-4 rounded-lg border-2 transition-all ${
        active
          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/10'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className={`text-sm font-semibold ${active ? 'text-emerald-700' : 'text-gray-900'}`}>
        {label}
      </div>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>
    </button>
  );
}
